// src/components/customer/PortalMedia.jsx
// Hero and gallery items for the Customer Portal (and the admin Live Preview). Items are image or
// MP4 video URLs.
//   Hero video:    autoplays and loops. It plays with its sound where the browser allows it; browsers
//                  that block sound until the customer interacts start it muted and turn the sound on
//                  at the customer's first tap/click/key press. It is muted as soon as the customer
//                  plays any gallery video, and stays muted until they switch it back on with the
//                  speaker button.
//   Gallery video: does not autoplay. Shows its first frame with a play button; the customer taps to
//                  play it with sound. Only one gallery video plays at a time.
import React, { useEffect, useRef, useState } from 'react';
import { isVideoUrl } from '../../utils/mediaType';
import '../../styles/portal-media.css';

const GALLERY_PLAY_EVENT = 'portal-gallery-video-play';

// Customers who asked for less motion or less data get a still frame instead of an autoplaying video
const prefersStillHero = () => {
  try {
    return Boolean(
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ||
      navigator.connection?.saveData
    );
  } catch (e) {
    return false;
  }
};

// "#t=0.1" makes browsers (iOS Safari included) draw the first frame instead of a black box
const firstFrame = (src) => `${src}#t=0.1`;

const GESTURE_EVENTS = ['pointerdown', 'touchend', 'click', 'keydown'];

export function HeroMedia({ src, className = 'salon-hero-image', alt = 'Salon Hero' }) {
  const videoRef = useRef(null);
  const still = useRef(prefersStillHero()).current;
  const video = isVideoUrl(src);
  const wantsMuted = useRef(false); // set once the customer plays a gallery video or mutes the hero
  const [soundOn, setSoundOn] = useState(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || still) return undefined;

    wantsMuted.current = false;
    const play = () => {
      const attempt = el.play();
      if (attempt && attempt.catch) attempt.catch(() => { /* blocked by the browser; stays on first frame */ });
    };
    const onVolumeChange = () => setSoundOn(!el.muted);
    el.addEventListener('volumechange', onVolumeChange);

    // Turns the sound on at the customer's first interaction (unless they've asked for it off)
    const unlockSound = () => {
      if (wantsMuted.current || !el.muted) return;
      el.muted = false;
      if (el.paused) play();
    };
    const removeUnlock = () => GESTURE_EVENTS.forEach(ev => document.removeEventListener(ev, unlockSound));

    // Try with sound first. If the browser refuses, play muted and wait for a first interaction.
    el.muted = false;
    const first = el.play();
    const startMuted = () => {
      el.muted = true;
      play();
      GESTURE_EVENTS.forEach(ev => document.addEventListener(ev, unlockSound));
    };
    if (first && first.catch) first.catch(startMuted);

    // A gallery video starting mutes the hero (the hero keeps playing)
    const onGalleryPlay = (e) => {
      if (!e.detail) return;
      wantsMuted.current = true;
      el.muted = true;
      removeUnlock();
    };
    window.addEventListener(GALLERY_PLAY_EVENT, onGalleryPlay);

    // Some mobile browsers pause other media when audio starts elsewhere; keep the hero running
    const onPause = () => {
      if (el.ended || document.visibilityState !== 'visible') return;
      setTimeout(() => { if (el.paused) play(); }, 200);
    };
    el.addEventListener('pause', onPause);

    return () => {
      el.removeEventListener('volumechange', onVolumeChange);
      el.removeEventListener('pause', onPause);
      window.removeEventListener(GALLERY_PLAY_EVENT, onGalleryPlay);
      removeUnlock();
    };
  }, [src, still]);

  const toggleSound = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.muted) {
      wantsMuted.current = false;
      el.muted = false;
      if (el.paused) el.play().catch(() => {});
      // One thing makes sound at a time: stop any gallery video that is playing
      window.dispatchEvent(new CustomEvent(GALLERY_PLAY_EVENT, { detail: null }));
    } else {
      wantsMuted.current = true;
      el.muted = true;
    }
  };

  if (!video) return <img src={src} alt={alt} className={className} />;

  return (
    <div className="portal-hero-media">
      <video
        ref={videoRef}
        className={className}
        src={still ? firstFrame(src) : src}
        aria-label={alt}
        muted
        loop
        playsInline
        autoPlay={!still}
        preload={still ? 'metadata' : 'auto'}
      />
      {!still && (
        <button
          type="button"
          className="portal-hero-sound-toggle"
          onClick={toggleSound}
          aria-label={soundOn ? 'Mute video' : 'Turn video sound on'}
          aria-pressed={soundOn}
        >
          {soundOn ? '🔊' : '🔇'}
        </button>
      )}
    </div>
  );
}

function GalleryVideo({ src, alt, className }) {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [failed, setFailed] = useState(false);

  // Starting one gallery video pauses any other
  useEffect(() => {
    const onOtherPlay = (e) => {
      if (videoRef.current && e.detail !== videoRef.current) videoRef.current.pause();
    };
    window.addEventListener(GALLERY_PLAY_EVENT, onOtherPlay);
    return () => window.removeEventListener(GALLERY_PLAY_EVENT, onOtherPlay);
  }, []);

  const play = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = false; // gallery videos play with sound (the tap is the user gesture browsers require)
    el.volume = 1;
    window.dispatchEvent(new CustomEvent(GALLERY_PLAY_EVENT, { detail: el }));
    setStarted(true);
    const attempt = el.play();
    if (attempt && attempt.catch) attempt.catch(() => setStarted(false));
  };

  if (failed) {
    return (
      <div className={`${className} salon-gallery-video-tile salon-gallery-video-failed`}>
        Video unavailable
      </div>
    );
  }

  return (
    <div className={`${className} salon-gallery-video-tile`}>
      <video
        ref={videoRef}
        src={firstFrame(src)}
        aria-label={alt}
        playsInline
        preload="metadata"
        controls={started}
        onEnded={() => {
          if (videoRef.current) videoRef.current.currentTime = 0;
          setStarted(false);
        }}
        onError={() => setFailed(true)}
      />
      {!started && (
        <button type="button" className="salon-gallery-video-play" onClick={play} aria-label={`Play video: ${alt}`}>
          ▶
        </button>
      )}
    </div>
  );
}

export function GalleryMedia({ src, alt, className = 'salon-gallery-image' }) {
  if (!isVideoUrl(src)) return <img src={src} alt={alt} className={className} />;
  return <GalleryVideo src={src} alt={alt} className={className} />;
}
